import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Heart, ArrowRight, Check, Brain, Target, Sparkles, Users, MessageCircle, Home as HomeIcon, Compass } from 'lucide-react';
import { toast } from 'sonner';

export default function SinglesOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  
  // User data
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<'man' | 'woman' | 'non-binary' | 'prefer-not-to-say'>('prefer-not-to-say');
  const [lookingFor, setLookingFor] = useState<'man' | 'woman' | 'any'>('any');
  
  // Core values and priorities
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [lifeGoals, setLifeGoals] = useState<string[]>([]);
  const [dealBreakers, setDealBreakers] = useState<string[]>([]);
  
  // Communication and emotional style
  const [communicationStyle, setCommunicationStyle] = useState<'direct' | 'subtle' | 'balanced' | 'unsure'>('balanced');
  const [conflictStyle, setConflictStyle] = useState<'address-immediately' | 'need-space' | 'avoid' | 'unsure'>('unsure');
  const [affectionStyle, setAffectionStyle] = useState<'verbal' | 'physical' | 'acts-of-service' | 'quality-time' | 'gifts'>('quality-time');
  
  // Lifestyle preferences
  const [socialPreference, setSocialPreference] = useState(50); // 0-100 scale (introvert to extrovert)
  const [lifestyleType, setLifestyleType] = useState<'spontaneous' | 'planned' | 'flexible'>('flexible');
  const [activityLevel, setActivityLevel] = useState(50); // 0-100 scale (homebody to adventurous)
  
  // Relationship patterns
  const [attachmentStyle, setAttachmentStyle] = useState<'secure' | 'anxious' | 'avoidant' | 'fearful' | 'unsure'>('unsure');
  const [pastPatterns, setPastPatterns] = useState<string[]>([]);
  const [learnings, setLearnings] = useState('');
  
  // Ideal partner traits (weighted)
  const [importanceRatings, setImportanceRatings] = useState({
    emotionalIntelligence: 50,
    ambition: 50,
    humor: 50,
    stability: 50,
    adventure: 50,
    communication: 50,
    physicalAttraction: 50,
    sharedValues: 50,
  });

  const handleComplete = () => {
    // Create compatibility profile
    const compatibilityProfile = {
      mode: 'single',
      demographics: {
        age,
        gender,
        lookingFor,
      },
      values: {
        coreValues,
        lifeGoals,
        dealBreakers,
      },
      communication: {
        style: communicationStyle,
        conflictStyle,
        affectionStyle,
      },
      lifestyle: {
        socialPreference,
        lifestyleType,
        activityLevel,
      },
      patterns: {
        attachmentStyle,
        pastPatterns,
        learnings,
      },
      idealPartner: importanceRatings,
      createdAt: new Date().toISOString(),
    };

    // Calculate compatibility insights
    const compatibilityInsights = generateCompatibilityInsights(compatibilityProfile);

    localStorage.setItem('tension_mode', 'single');
    localStorage.setItem('tension_compatibility_profile', JSON.stringify(compatibilityProfile));
    localStorage.setItem('tension_compatibility_insights', JSON.stringify(compatibilityInsights));
    localStorage.setItem('tension_onboarding_complete', 'true');

    toast.success('Your relationship-readiness profile is ready!');
    navigate('/compatibility-profile', { replace: true });
  };

  // Generate initial insights based on profile
  const generateCompatibilityInsights = (profile: any) => {
    const insights = {
      topPriorities: [],
      compatibilityFactors: [],
      redFlags: [],
      greenFlags: [],
      datingStrategy: '',
      attachmentGuidance: '',
    };

    // Top priorities based on importance ratings
    const sortedPriorities = Object.entries(profile.idealPartner)
      .sort(([, a]: any, [, b]: any) => b - a)
      .slice(0, 3)
      .map(([key]) => key);
    
    insights.topPriorities = sortedPriorities;

    // Compatibility factors based on selections
    insights.compatibilityFactors = [
      `Values alignment: ${profile.values.coreValues.length} core values identified`,
      `Communication style: ${profile.communication.style}`,
      `Social compatibility: ${profile.lifestyle.socialPreference > 50 ? 'Extroverted' : 'Introverted'} leaning`,
    ];

    // Red flags based on past patterns
    if (profile.patterns.pastPatterns.includes('attract_unavailable')) {
      insights.redFlags.push('Watch for emotional unavailability');
    }
    if (profile.patterns.pastPatterns.includes('over_pursue')) {
      insights.redFlags.push('Notice if you\'re doing all the initiating');
    }

    // Green flags based on values
    if (profile.values.coreValues.includes('honesty')) {
      insights.greenFlags.push('Open and direct communication');
    }
    if (profile.values.coreValues.includes('growth')) {
      insights.greenFlags.push('Growth mindset and self-awareness');
    }

    // Dating strategy based on attachment
    if (profile.patterns.attachmentStyle === 'anxious') {
      insights.datingStrategy = 'Focus on self-regulation and avoid over-pursuing. Prioritize secure connection patterns and consistent communication.';
    } else if (profile.patterns.attachmentStyle === 'avoidant') {
      insights.datingStrategy = 'Practice vulnerability gradually. Notice when you pull away and communicate your needs for space.';
    } else {
      insights.datingStrategy = 'Continue building self-awareness and trust your gut when evaluating alignment.';
    }

    // Attachment-specific guidance
    insights.attachmentGuidance = getAttachmentGuidance(profile.patterns.attachmentStyle);

    return insights;
  };

  const getAttachmentGuidance = (style: string) => {
    const guidance: Record<string, string> = {
      secure: 'You likely have healthy relationship patterns. Focus on finding someone aligned with your emotional maturity.',
      anxious: 'You may benefit from partners who are consistent, communicative, and reassuring. Avoid avoidant types who trigger your anxiety.',
      avoidant: 'Look for patient partners who respect your need for independence. Work on recognizing when you withdraw and communicating that.',
      fearful: 'You need someone emotionally stable who can provide both closeness and space. Therapy may help with push-pull patterns.',
      unsure: 'As you date, notice your patterns around intimacy, conflict, and communication to understand your style better.',
    };
    return guidance[style] || guidance.unsure;
  };

  return (
    <div className="min-h-screen tension-page-bg flex items-center justify-center px-6 py-12">
      <div className="max-w-lg w-full space-y-6">
        {/* Logo/Header */}
        <header className="text-center space-y-2" role="banner">
          <h1 className="text-4xl font-light tracking-wide text-neutral-900">TENSION</h1>
          <p className="text-neutral-600">Understand your relationship-readiness alignment</p>
        </header>

        {/* Progress */}
        <nav aria-label="Onboarding progress" className="flex items-center justify-center gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
            <div
              key={s}
              role="progressbar"
              aria-valuenow={step}
              aria-valuemin={1}
              aria-valuemax={8}
              aria-label={`Step ${s} of 8`}
              className={`h-1.5 rounded-full transition-all ${
                s === step ? 'w-12 bg-violet-600' : s < step ? 'w-8 bg-violet-400' : 'w-8 bg-neutral-200'
              }`}
            />
          ))}
        </nav>

        {/* Step 1: Welcome */}
        {step === 1 && (
          <Card className="p-8 space-y-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-violet-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-violet-600" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-light text-neutral-900">Build Your Relationship Readiness Profile</h2>
                <p className="text-neutral-600">
                  Answer deep questions about yourself to understand which relationship dynamics align with you.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Brain className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900">Self-Discovery</p>
                    <p className="text-xs text-neutral-600">
                      Understand your values, communication style, and relationship patterns
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900">Readiness Profile</p>
                    <p className="text-xs text-neutral-600">
                      Get a detailed profile of the relationship qualities that matter most to you
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Compass className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900">Connection Strategy</p>
                    <p className="text-xs text-neutral-600">
                      Personalized guidance on red flags, green flags, and approach
                    </p>
                  </div>
                </div>
              </div>

              <div className="tension-glow bg-violet-50 border border-violet-200 rounded-lg p-4">
                <p className="text-xs text-violet-900">
                  <strong>Privacy-first:</strong> Your data stays private and helps you make better relationship decisions.
                </p>
              </div>
            </div>

            <Button onClick={() => setStep(2)} className="w-full" size="lg">
              Start Assessment
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Card>
        )}

        {/* Step 2: Basic Demographics */}
        {step === 2 && (
          <Card className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-light text-neutral-900">About You</h2>
              <p className="text-sm text-neutral-600">
                Basic information to personalize your experience
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  Age Range
                </label>
                <Input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="e.g., 28"
                  min="18"
                  max="99"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  I identify as
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['woman', 'man', 'non-binary', 'prefer-not-to-say'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setGender(type)}
                      className={`px-4 py-3 rounded-lg text-sm transition-colors ${
                        gender === type
                          ? 'bg-violet-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                    >
                      {type === 'prefer-not-to-say' ? 'Prefer not to say' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  Looking for
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['woman', 'man', 'any'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setLookingFor(type)}
                      className={`px-4 py-3 rounded-lg text-sm transition-colors ${
                        lookingFor === type
                          ? 'bg-violet-600 text-white'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                      }`}
                    >
                      {type === 'any' ? 'Anyone' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button onClick={() => setStep(3)} className="flex-1">
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 3: Core Values */}
        {step === 3 && (
          <Card className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-pink-100 rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-pink-600" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-light text-neutral-900">Core Values</h2>
              <p className="text-sm text-neutral-600">
                Select the values that are non-negotiable for you (select 3-5)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'honesty', label: 'Honesty' },
                { id: 'loyalty', label: 'Loyalty' },
                { id: 'growth', label: 'Personal Growth' },
                { id: 'family', label: 'Family' },
                { id: 'career', label: 'Career Ambition' },
                { id: 'adventure', label: 'Adventure' },
                { id: 'stability', label: 'Stability' },
                { id: 'creativity', label: 'Creativity' },
                { id: 'spirituality', label: 'Spirituality' },
                { id: 'health', label: 'Health/Fitness' },
                { id: 'community', label: 'Community' },
                { id: 'independence', label: 'Independence' },
              ].map((value) => {
                const isSelected = coreValues.includes(value.id);
                return (
                  <button
                    key={value.id}
                    onClick={() => {
                      if (isSelected) {
                        setCoreValues(coreValues.filter(v => v !== value.id));
                      } else {
                        setCoreValues([...coreValues, value.id]);
                      }
                    }}
                    className={`px-4 py-3 rounded-lg text-sm transition-all ${
                      isSelected
                        ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                        : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100'
                    }`}
                  >
                    {value.label}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button 
                onClick={() => setStep(4)} 
                className="flex-1"
                disabled={coreValues.length < 3}
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 4: Life Goals */}
        {step === 4 && (
          <Card className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8 text-blue-600" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-light text-neutral-900">Life Goals</h2>
              <p className="text-sm text-neutral-600">
                What are you working towards? (select all that apply)
              </p>
            </div>

            <div className="space-y-2">
              {[
                { id: 'marriage', label: 'Eventually get married' },
                { id: 'children', label: 'Have children' },
                { id: 'no-children', label: 'Prefer not to have children' },
                { id: 'career-growth', label: 'Major career advancement' },
                { id: 'travel', label: 'Travel extensively' },
                { id: 'home-ownership', label: 'Own a home' },
                { id: 'entrepreneurship', label: 'Start a business' },
                { id: 'relocation', label: 'Open to relocating' },
                { id: 'education', label: 'Further education' },
                { id: 'financial-freedom', label: 'Financial independence' },
              ].map((goal) => {
                const isSelected = lifeGoals.includes(goal.id);
                return (
                  <button
                    key={goal.id}
                    onClick={() => {
                      if (isSelected) {
                        setLifeGoals(lifeGoals.filter(g => g !== goal.id));
                      } else {
                        setLifeGoals([...lifeGoals, goal.id]);
                      }
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-all ${
                      isSelected
                        ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                        : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100'
                    }`}
                  >
                    {goal.label}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(3)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button onClick={() => setStep(5)} className="flex-1">
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 5: Communication Style */}
        {step === 5 && (
          <Card className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-purple-100 rounded-full flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-purple-600" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-light text-neutral-900">Communication Style</h2>
              <p className="text-sm text-neutral-600">
                How do you prefer to communicate and handle emotions?
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  Communication preference
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'direct', label: 'Direct', desc: 'Say what I mean, address issues head-on' },
                    { id: 'subtle', label: 'Subtle', desc: 'Hint at things, prefer indirect approach' },
                    { id: 'balanced', label: 'Balanced', desc: 'Depends on the situation' },
                    { id: 'unsure', label: 'Not sure', desc: 'Still figuring this out' },
                  ].map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setCommunicationStyle(style.id as any)}
                      className={`w-full text-left p-4 rounded-lg transition-all ${
                        communicationStyle === style.id
                          ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                          : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100'
                      }`}
                    >
                      <p className="font-medium mb-1">{style.label}</p>
                      <p className={`text-xs ${communicationStyle === style.id ? 'text-violet-100' : 'text-neutral-600'}`}>
                        {style.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  When there's conflict, I prefer to
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'address-immediately', label: 'Address it immediately' },
                    { id: 'need-space', label: 'Take space then discuss' },
                    { id: 'avoid', label: 'Avoid conflict if possible' },
                    { id: 'unsure', label: 'It varies' },
                  ].map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setConflictStyle(style.id as any)}
                      className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-all ${
                        conflictStyle === style.id
                          ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                          : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100'
                      }`}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  I feel most loved through
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'verbal', label: 'Words of affirmation' },
                    { id: 'physical', label: 'Physical touch' },
                    { id: 'acts-of-service', label: 'Acts of service' },
                    { id: 'quality-time', label: 'Quality time' },
                    { id: 'gifts', label: 'Gifts' },
                  ].map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setAffectionStyle(style.id as any)}
                      className={`px-4 py-3 rounded-lg text-sm transition-all ${
                        affectionStyle === style.id
                          ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                          : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100'
                      }`}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(4)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button onClick={() => setStep(6)} className="flex-1">
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 6: Lifestyle Preferences */}
        {step === 6 && (
          <Card className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <HomeIcon className="w-8 h-8 text-green-600" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-light text-neutral-900">Lifestyle Alignment</h2>
              <p className="text-sm text-neutral-600">
                Help us understand your daily life preferences
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-neutral-900 mb-3 block">
                  Social energy: How do you recharge?
                </label>
                <div className="space-y-2">
                  <Slider
                    value={[socialPreference]}
                    onValueChange={(value) => setSocialPreference(value[0])}
                    min={0}
                    max={100}
                    step={10}
                  />
                  <div className="flex justify-between text-xs text-neutral-600">
                    <span>Introvert (recharge alone)</span>
                    <span>Extrovert (recharge with people)</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-900 mb-3 block">
                  Activity level: What's your ideal weekend?
                </label>
                <div className="space-y-2">
                  <Slider
                    value={[activityLevel]}
                    onValueChange={(value) => setActivityLevel(value[0])}
                    min={0}
                    max={100}
                    step={10}
                  />
                  <div className="flex justify-between text-xs text-neutral-600">
                    <span>Homebody (cozy & relaxed)</span>
                    <span>Adventurous (always exploring)</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  Planning style
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'planned', label: 'Planned', desc: 'I like to schedule everything' },
                    { id: 'spontaneous', label: 'Spontaneous', desc: 'I prefer going with the flow' },
                    { id: 'flexible', label: 'Flexible', desc: 'Mix of both' },
                  ].map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setLifestyleType(style.id as any)}
                      className={`w-full text-left p-4 rounded-lg transition-all ${
                        lifestyleType === style.id
                          ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                          : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100'
                      }`}
                    >
                      <p className="font-medium mb-1">{style.label}</p>
                      <p className={`text-xs ${lifestyleType === style.id ? 'text-violet-100' : 'text-neutral-600'}`}>
                        {style.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(5)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button onClick={() => setStep(7)} className="flex-1">
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 7: Attachment & Patterns */}
        {step === 7 && (
          <Card className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-pink-100 rounded-full flex items-center justify-center">
                <Brain className="w-8 h-8 text-pink-600" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-light text-neutral-900">Relationship Patterns</h2>
              <p className="text-sm text-neutral-600">
                Understanding your patterns helps you choose healthier connections
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  Attachment Style
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'secure', label: 'Secure', desc: 'Comfortable with intimacy and independence' },
                    { id: 'anxious', label: 'Anxious', desc: 'Crave closeness, fear abandonment' },
                    { id: 'avoidant', label: 'Avoidant', desc: 'Value independence, uncomfortable with vulnerability' },
                    { id: 'fearful', label: 'Fearful-Avoidant', desc: 'Want intimacy but fear getting hurt' },
                    { id: 'unsure', label: 'Not Sure', desc: 'Still learning about myself' },
                  ].map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setAttachmentStyle(style.id as any)}
                      className={`w-full text-left p-4 rounded-lg transition-all ${
                        attachmentStyle === style.id
                          ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                          : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100'
                      }`}
                    >
                      <p className="font-medium mb-1">{style.label}</p>
                      <p className={`text-xs ${attachmentStyle === style.id ? 'text-violet-100' : 'text-neutral-600'}`}>
                        {style.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  Past relationship patterns (select all that apply)
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'over_pursue', label: 'I tend to over-pursue and chase' },
                    { id: 'anxious_texting', label: 'I overthink texts and response times' },
                    { id: 'lose_myself', label: 'I lose myself in relationships' },
                    { id: 'need_reassurance', label: 'I need constant reassurance' },
                    { id: 'attract_unavailable', label: 'I attract emotionally unavailable people' },
                    { id: 'self_sabotage', label: 'I tend to self-sabotage good things' },
                  ].map((pattern) => {
                    const isSelected = pastPatterns.includes(pattern.id);
                    return (
                      <button
                        key={pattern.id}
                        onClick={() => {
                          if (isSelected) {
                            setPastPatterns(pastPatterns.filter(p => p !== pattern.id));
                          } else {
                            setPastPatterns([...pastPatterns, pattern.id]);
                          }
                        }}
                        className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-all ${
                          isSelected
                            ? 'bg-violet-600 text-white ring-2 ring-violet-400'
                            : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100'
                        }`}
                      >
                        {pattern.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-neutral-900 mb-2 block">
                  What have you learned about yourself? (optional)
                </label>
                <Textarea
                  value={learnings}
                  onChange={(e) => setLearnings(e.target.value)}
                  placeholder="e.g., I've learned that I need more independence than I thought, or I realize I struggle with vulnerability..."
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-neutral-500 mt-1">
                  {learnings.length}/500 characters
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(6)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button onClick={() => setStep(8)} className="flex-1">
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 8: Ideal Partner Priorities */}
        {step === 8 && (
          <Card className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                <Users className="w-8 h-8 text-amber-600" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-light text-neutral-900">Ideal Relationship Traits</h2>
              <p className="text-sm text-neutral-600">
                Rate how important each trait is to you (0 = not important, 100 = essential)
              </p>
            </div>

            <div className="space-y-5">
              {Object.entries({
                emotionalIntelligence: 'Emotional Intelligence',
                communication: 'Communication Skills',
                sharedValues: 'Shared Core Values',
                stability: 'Emotional Stability',
                humor: 'Sense of Humor',
                ambition: 'Career Ambition',
                adventure: 'Adventurousness',
                physicalAttraction: 'Physical Attraction',
              }).map(([key, label]) => (
                <div key={key}>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-neutral-900">{label}</label>
                    <span className="text-sm text-violet-600 font-medium">
                      {importanceRatings[key as keyof typeof importanceRatings]}%
                    </span>
                  </div>
                  <Slider
                    value={[importanceRatings[key as keyof typeof importanceRatings]]}
                    onValueChange={(value) => 
                      setImportanceRatings({ ...importanceRatings, [key]: value[0] })
                    }
                    min={0}
                    max={100}
                    step={5}
                  />
                </div>
              ))}
            </div>

            <div className="tension-glow bg-violet-50 border border-violet-200 rounded-lg p-4">
              <p className="text-xs text-violet-900">
                <strong>Your top priorities:</strong> We'll use these ratings to build your readiness profile and suggest aligned connection styles.
              </p>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(7)} variant="outline" className="flex-1">
                Back
              </Button>
              <Button onClick={handleComplete} className="flex-1">
                Complete Assessment
                <Check className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {/* Skip option */}
        {step < 8 && (
          <button
            onClick={() => navigate('/auth')}
            className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors text-center w-full"
          >
            I already have an account
          </button>
        )}
      </div>
    </div>
  );
}